<template>
  <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
    <router-link :to="{ name: 'pages.admin.dashboard' }"
      class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
      <icon icon="solar:home-smile-bold-duotone" /> <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard")
        }}</span>
    </router-link>
    <span class="tw-text-gray-300">/</span>
    <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2"><span class="tw-text-sm">{{
      $t("dashboard.sidebar.users") }}</span></button>
  </div>

  <div class="tw-p-2">
    <div
      class="tw-h-full tw-w-full tw-p-4x tw-bg-whitex tw-rounded tw-borderx tw-border-b-2 tw-min-h-[calc(100dvh-125px)]">
      
      <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
        <div class="tw-flex tw-items-center tw-gap-2">
          <FormField 
            v-model="search" 
            type="text" 
            :hide-label="true" 
            :hide-errors="true" 
            :placeholder="$t('common.users-search-placeholder', 'Search by ID, Name...')"
            left-icon="solar:magnifer-linear" 
            @input="onSearch" 
            class="tw-bg-white tw-rounded-md tw-min-w-[300px]" 
          />

          <!-- Status Filter -->
          <FormSelect 
            v-model="filters.status" 
            :options="statusOptions"
            class="tw-bg-white tw-rounded-md tw-min-w-[150px]"
          />

          <!-- Role Filter -->
          <FormSelect 
            v-model="filters.roles" 
            :options="roleOptions"
            class="tw-bg-white tw-rounded-md tw-min-w-[150px]"
          />

          <!-- Company Filter -->
          <FormSelect 
            v-model="filters.companies" 
            :options="companyOptions"
            class="tw-bg-white tw-rounded-md tw-min-w-[150px]"
          />

        </div>

        <div>
          <user-create-popup />
        </div>
      </div>
      
      <!-- Table Section -->
      <div>
        <UserTable :loading="loading" :items="users" :options="paginationOptions" @next="onNext" @previous="onPrevious" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, provide, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { paginate } from '@/api/users'
import FormField from "@/components/form/FormField"
import FormSelect from '@/components/form/FormSelect'
import UserTable from '@/views/admin/users/partials/table/UserTable'
import UserCreatePopup from './partials/UserCreatePopup'
import { all as rolesAll } from '@/api/roles'
import { admin } from '@/api/companies'
import { useRoleStore } from '@/stores'
import useAlert from '@/composables/useAlert'

const { t } = useI18n()
const roleStore = useRoleStore()

// Reactive data
const loading = ref(false)
const users = ref([])
const search = ref('')

// Pagination options
const paginationOptions = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  last_page: 1,
  next_page_url: null,
  prev_page_url: null
})

// Filters
const filters = reactive({
  status: 'all',
  roles: 'all',
  companies: 'all'
})

// Status options for filter
const statusOptions = ref([
  { id: 'all', name: t('common.all-status', 'All Status') },
  { id: 'active', name: t('common.active', 'Active') },
  { id: 'inactive', name: t('common.inactive', 'Inactive') },
  // { id: 'pending', name: t('common.pending', 'Pending') }
])

// Role options for filter
const roleOptions = computed(() => [
  { id: 'all', name: t('common.all-roles', 'All Roles') },
  ...roleStore.roles.map(role => ({
    id: role.id,
    name: role.name.charAt(0).toUpperCase() + role.name.slice(1).toLowerCase()
  }))
])

// Company options for filter
const companyOptions = ref([
  { id: 'all', name: t('common.all-companies', 'All Companies') }
])

// Search debounce
let searchTimeout = null
let filterTimeout = null

// Watch for filter changes
watch(() => filters.status, () => {
  clearTimeout(filterTimeout)
  filterTimeout = setTimeout(() => {
    fetchUsers(1)
  }, 300)
}, { immediate: false })

watch(() => filters.roles, () => {
  clearTimeout(filterTimeout)
  filterTimeout = setTimeout(() => {
    fetchUsers(1)
  }, 300)
}, { immediate: false })

watch(() => filters.companies, () => {
  clearTimeout(filterTimeout)
  filterTimeout = setTimeout(() => {
    fetchUsers(1)
  }, 300)
}, { immediate: false })

async function fetchUsers(page = 1) {
  loading.value = true
  users.value = []

  try {
    const params = {
      page,
      per_page: paginationOptions.value.per_page,
      search: search.value,
      ...(filters.status && filters.status !== 'all' && { status: filters.status }),
      ...(filters.roles && filters.roles !== 'all' && { roles: filters.roles }),
      ...(filters.companies && filters.companies !== 'all' && { companies: filters.companies })
    }

    const response = await paginate(params)
    console.log('User api response', response.data.data.data);
  
    if (response.data.data && response.data.data.data) {
      users.value = response.data.data.data
      paginationOptions.value = {
        current_page: response.data.data.current_page,
        per_page: response.data.data.per_page,
        total: response.data.data.total,
        last_page: response.data.data.last_page,
        next_page_url: response.data.data.next_page_url,
        prev_page_url: response.data.data.prev_page_url
      }
    }
  } catch (error) {
    console.error('Error fetching users:', error)
    useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger')
  } finally {
    loading.value = false
  }
}

const onSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    fetchUsers(1)
  }, 500)
}

async function fetchCompanies() {
  try {
    const response = await admin.all()
    if (response.data && response.data.companies) {
      companyOptions.value = [
        { id: 'all', name: t('common.all-companies', 'All Companies') },
        ...response.data.companies.map(company => ({
          id: company.id,
          name: company.name.charAt(0).toUpperCase() + company.name.slice(1).toLowerCase()
        }))
      ]
    }
  } catch (error) {
    console.error('Error fetching companies:', error)
  }
}

const onNext = () => {
  if (paginationOptions.value.current_page < paginationOptions.value.last_page) {
    fetchUsers(paginationOptions.value.current_page + 1)
  }
}

const onPrevious = () => {
  if (paginationOptions.value.current_page > 1) {
    fetchUsers(paginationOptions.value.current_page - 1)
  }
}

const onCreated = (user) => {
  users.value.unshift(user)
}

const onUpdated = (user) => {
  const index = users.value.findIndex(u => u.id === user.id)
  if (index !== -1) {
    users.value[index] = user
  }
}

const onDeleted = (userId) => {
  const index = users.value.findIndex(u => u.id === userId)
  if (index !== -1) {
    users.value.splice(index, 1)
  }
}

provide('onCreated', onCreated)
provide('onUpdated', onUpdated)
provide('onDeleted', onDeleted)

// Initialize
onMounted(() => {
  fetchUsers()
  if (!roleStore.fetched) {
    // Fetch roles if not already fetched
    rolesAll().then(({ data }) => {
      roleStore.setRoles(data.roles)
      roleStore.setFetched(true)
    })
  }
  fetchCompanies()
})
</script>

<style></style>